import React,{useState,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const {FiLock,FiEye,FiCopy,FiCheck,FiPlus,FiList,FiExternalLink,FiCheckCircle,FiAlertCircle,FiX,FiImage,FiVideo,FiPhone,FiSend,FiMail,FiMessageSquare,FiChevronDown,FiMapPin,FiArrowUp,FiArrowDown,FiTrash2,FiInstagram}=FiIcons;
import {cmsService} from '../services/cmsService';
import IntroOutreachComposer from '../components/preview/IntroOutreachComposer';
import TransformationSnapshotStudio from '../components/preview/TransformationSnapshotStudio';
const DEFAULT_PHONE_PREFIX='+61';
const PREVIEW_ADMIN_SESSION_KEY='bpa_preview_admin_session_v1';
const buildDefaultPageTitle=(clientName)=> {
  const normalizedName=clientName.trim();
  return normalizedName ? `${normalizedName} | Private Jewellery Visual Preview` : '';
};
const buildDefaultIntroText=(clientName)=> {
  const normalizedName=clientName.trim();
  if (!normalizedName) {
    return '';
  }
  return `Thank you for reviewing your private jewellery visual preview for ${normalizedName}. These watermarked concepts have been prepared to demonstrate how selected jewellery pieces can be presented through premium model-worn imagery and short-form motion content. Please review the visuals below and approve the preview or request changes when ready.`;
};
const normalizeAustralianPhone=(value)=> {
  const normalizedValue=value.trim();
  if (!normalizedValue) {
    return DEFAULT_PHONE_PREFIX;
  }
  const compactValue=normalizedValue.replace(/[()\s-]/g,'');
  if (compactValue===DEFAULT_PHONE_PREFIX) {
    return DEFAULT_PHONE_PREFIX;
  }
  if (compactValue.startsWith('+610')) {
    return `+61${compactValue.slice(4).replace(/\D/g,'')}`;
  }
  if (compactValue.startsWith('04')) {
    return `+61${compactValue.slice(1).replace(/\D/g,'')}`;
  }
  if (compactValue.startsWith('61')) {
    return `+${compactValue.replace(/\D/g,'')}`;
  }
  if (compactValue.startsWith('+')) {
    return `+${compactValue.slice(1).replace(/\D/g,'')}`;
  }
  const digitsOnly=compactValue.replace(/\D/g,'');
  if (digitsOnly.startsWith('0')) {
    return `+61${digitsOnly.slice(1)}`;
  }
  return `+61${digitsOnly}`;
};
const normalizeSmsRecipientList=(value)=> {
  const rawRecipients=value
    .split(/[\n,;]+/)
    .map((recipient)=> recipient.trim())
    .filter(Boolean);
  if (!rawRecipients.length) {
    return DEFAULT_PHONE_PREFIX;
  }
  const normalizedRecipients=rawRecipients
    .map((recipient)=> normalizeAustralianPhone(recipient))
    .filter(Boolean);
  return [...new Set(normalizedRecipients)].join('\n');
};
const getValidSmsRecipients=(value)=> value
  .split(/[\n,;]+/)
  .map((recipient)=> normalizeAustralianPhone(recipient))
  .filter((recipient)=> /^\+\d{8,15}$/.test(recipient));
const normalizeEmailRecipientList=(value)=> {
  const normalizedRecipients=value
    .split(/[\n,;]+/)
    .map((recipient)=> recipient.trim().toLowerCase())
    .filter(Boolean);
  return [...new Set(normalizedRecipients)].join('\n');
};
const savePreviewAdminSession=(passcode)=> {
  try {
    sessionStorage.setItem(PREVIEW_ADMIN_SESSION_KEY,JSON.stringify({
      adminPasscode: passcode,
      savedAt: Date.now()
    }));
  } catch (e) {
    console.warn('[PreviewPortal] Failed to save admin session:',e);
  }
};
const readPreviewAdminSession=()=> {
  try {
    const stored=sessionStorage.getItem(PREVIEW_ADMIN_SESSION_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (e) {
    console.warn('[PreviewPortal] Failed to read admin session:',e);
    return null;
  }
};
const clearPreviewAdminSession=()=> {
  try {
    sessionStorage.removeItem(PREVIEW_ADMIN_SESSION_KEY);
  } catch (e) {
    console.warn('[PreviewPortal] Failed to clear admin session:',e);
  }
};
const PreviousPreviewMiniMedia=({previewId})=> {
  const [media,setMedia]=useState(null);
  const [isVideo,setIsVideo]=useState(false);
  useEffect(()=> {
    const loadPreviewMedia=async ()=> {
      try {
        const result=await cmsService.fetchPreview(previewId);
        if (result.success && result.data?.media?.[0]) {
          const firstMedia=result.data.media[0];
          setMedia(firstMedia);
          setIsVideo(
            String(firstMedia.type || '').toLowerCase()==='video' ||
            /\.(mp4|mov|webm|avi|m4v)(?:$|[?#])/i.test(firstMedia.url || '')
          );
        }
      } catch (error) {
        console.error('Failed to load media for previous preview:',error);
      }
    };
    loadPreviewMedia();
  },[previewId]);
  const handleContextMenu=(e)=> {
    e.preventDefault();
  };
  const handleDragStart=(e)=> {
    e.preventDefault();
  };
  if (!media) {
    return (
      <div className="w-20 h-[89px] rounded-soft border border-gold/30 bg-cream flex items-center justify-center overflow-hidden shadow-sm">
        <span className="text-[9px] text-muted uppercase tracking-widest">No Media</span>
      </div>
    );
  }
  return (
    <div className="w-20 h-[89px] rounded-soft border border-gold/30 overflow-hidden shadow-sm relative">
      {isVideo ? (
        <video
          src={media.url}
          className="w-full h-full object-cover opacity-90"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          disablePictureInPicture
          controlsList="nodownload noremoteplayback nofullscreen"
          onContextMenu={handleContextMenu}
          onDragStart={handleDragStart}
        />
      ) : (
        <img
          src={media.url}
          alt="Preview"
          className="w-full h-full object-cover"
          draggable={false}
          onContextMenu={handleContextMenu}
          onDragStart={handleDragStart}
          loading="lazy"
        />
      )}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="text-[8px] font-bold text-white tracking-widest uppercase bg-black/30 px-2 py-1 rounded-full">
          BPA PREVIEW
        </div>
      </div>
    </div>
  );
};
const MediaCard=({media,watermarkText})=> {
  const isVideo=String(media.type || '').toLowerCase()==='video' ||
    /\.(mp4|mov|webm|avi|m4v)(?:$|[?#])/i.test(media.url || '');
  const handleContextMenu=(e)=> {
    e.preventDefault();
  };
  const handleDragStart=(e)=> {
    e.preventDefault();
  };
  return (
    <div className="relative luxury-media-card aspect-[9/16] group overflow-hidden rounded-soft border border-white/10 shadow-luxury">
      <div className="absolute top-3 left-3 bg-ink/80 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 z-10 flex items-center gap-2">
        <SafeIcon icon={isVideo ? FiVideo : FiImage} className="w-3 h-3 text-gold" />
        <span className="text-[9px] uppercase tracking-widest text-soft">
          {isVideo ? 'Video' : 'Image'}
        </span>
      </div>
      {isVideo ? (
        <video
          src={media.url}
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
          autoPlay
          muted
          loop
          playsInline
          disablePictureInPicture
          controlsList="nodownload noremoteplayback nofullscreen"
          onContextMenu={handleContextMenu}
          onDragStart={handleDragStart}
        />
      ) : (
        <img
          src={media.url}
          alt="Preview media"
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
          draggable={false}
          onContextMenu={handleContextMenu}
          onDragStart={handleDragStart}
          loading="lazy"
        />
      )}
      <div className="absolute inset-[-25%] z-20 pointer-events-none overflow-hidden rotate-[-25deg]">
        <div className="flex flex-col gap-8 h-full items-center justify-center">
          {[...Array(7)].map((_,i)=> (
            <div key={i} className="text-center opacity-20 whitespace-nowrap">
              <div className="text-[11px] font-bold text-white tracking-widest uppercase bg-black/20 px-3 py-1 rounded-full">
                {watermarkText}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
const PreviewPortal=({mode,previewId})=> {
  const [adminPasscode,setAdminPasscode]=useState('');
  const [isAuthenticated,setIsAuthenticated]=useState(false);
  const [authRestoring,setAuthRestoring]=useState(mode==='admin');
  const [authError,setAuthError]=useState('');
  const [previews,setPreviews]=useState([]);
  const [loading,setLoading]=useState(false);
  const [successMessage,setSuccessMessage]=useState('');
  const [clientName,setClientName]=useState('');
  const [clientEmail,setClientEmail]=useState('');
  const [clientPhone,setClientPhone]=useState(DEFAULT_PHONE_PREFIX);
  const [mediaLinks,setMediaLinks]=useState('');
  const [pageTitle,setPageTitle]=useState('');
  const [introText,setIntroText]=useState('');
  const [watermarkText,setWatermarkText]=useState('BPA SOLUTIONS - WATERMARKED PREVIEW');
  const [pageTitleManuallyEdited,setPageTitleManuallyEdited]=useState(false);
  const [introTextManuallyEdited,setIntroTextManuallyEdited]=useState(false);
  const [editingPreviewId,setEditingPreviewId]=useState('');
  const [editingPreviewLoading,setEditingPreviewLoading]=useState(false);
  const [statusDialog,setStatusDialog]=useState(null);
  const [statusUpdating,setStatusUpdating]=useState(false);
  const [mediaReviewOpen,setMediaReviewOpen]=useState(false);
  const [pendingMediaRemovalIndex,setPendingMediaRemovalIndex]=useState(null);
  const [introOutreachOpen,setIntroOutreachOpen]=useState(false);
  const [transformationStudioOpen,setTransformationStudioOpen]=useState(false);
  const [outreachApprovalUpdating,setOutreachApprovalUpdating]=useState('');
  const [previewData,setPreviewData]=useState(null);
  const [previewError,setPreviewError]=useState('');
  const [clientMessage,setClientMessage]=useState('');
  const [clientActionLoading,setClientActionLoading]=useState('');
  const [clientActionError,setClientActionError]=useState('');
  const [showApprovalDialog,setShowApprovalDialog]=useState(false);
  const [approverName,setApproverName]=useState('');
  const [approverEmail,setApproverEmail]=useState('');
  const [approvalDialogError,setApprovalDialogError]=useState('');
  const [shareDialogOpen,setShareDialogOpen]=useState(false);
  const [selectedSharePreview,setSelectedSharePreview]=useState(null);
  const [shareDraftLoading,setShareDraftLoading]=useState(false);
  const [shareSending,setShareSending]=useState(false);
  const [shareEmailRecipients,setShareEmailRecipients]=useState('');
  const [shareSmsRecipients,setShareSmsRecipients]=useState(DEFAULT_PHONE_PREFIX);
  const [shareEmailSubject,setShareEmailSubject]=useState('');
  const [shareEmailMessage,setShareEmailMessage]=useState('');
  const [shareSmsMessage,setShareSmsMessage]=useState('');
  const [shareConsentConfirmed,setShareConsentConfirmed]=useState(false);
  const [shareConsentMethod,setShareConsentMethod]=useState('Phone call');
  const [shareStageMessages,setShareStageMessages]=useState([]);
  const [shareError,setShareError]=useState('');
  const [shareEmailTemplate,setShareEmailTemplate]=useState('standard');
  const [shareStandardEmailSubject,setShareStandardEmailSubject]=useState('');
  const [shareStandardEmailMessage,setShareStandardEmailMessage]=useState('');
  const [shareTrustEmailSubject,setShareTrustEmailSubject]=useState('');
  const [shareTrustEmailMessage,setShareTrustEmailMessage]=useState('');
  const parseEditableMediaLinks=()=> mediaLinks
    .split('\n')
    .map((url)=> url.trim())
    .filter(Boolean);
  const setEditableMediaLinks=(urls)=> {
    setMediaLinks(
      urls
        .map((url)=> url.trim())
        .filter(Boolean)
        .join('\n')
    );
  };
  const isLikelyVideoUrl=(url)=> /\.(mp4|mov|webm|avi|m4v)(?:$|[?#])/i.test(url || '');
  const moveEditableMedia=(index,direction)=> {
    const urls=parseEditableMediaLinks();
    const targetIndex=index + direction;
    if (targetIndex < 0 || targetIndex >=urls.length) {
      return;
    }
    const nextUrls=[...urls];
    const [movedUrl]=nextUrls.splice(index,1);
    nextUrls.splice(targetIndex,0,movedUrl);
    setEditableMediaLinks(nextUrls);
  };
  const requestEditableMediaRemoval=(index)=> {
    setPendingMediaRemovalIndex(index);
  };
  const cancelEditableMediaRemoval=()=> {
    setPendingMediaRemovalIndex(null);
  };
  const confirmEditableMediaRemoval=()=> {
    if (pendingMediaRemovalIndex===null) {
      return;
    }
    const urls=parseEditableMediaLinks();
    setEditableMediaLinks(urls.filter((_,index)=> index !==pendingMediaRemovalIndex));
    setPendingMediaRemovalIndex(null);
  };
  const createShareRequestId=()=> `share_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
  const appendShareStage=(message,status='info')=> {
    setShareStageMessages((current)=> [
      ...current,
      {
        id: `${Date.now()}_${Math.random()}`,
        message,
        status,
      },
    ]);
  };
  const applyShareEmailTemplate=(template)=> {
    setShareEmailTemplate(template);
    if (template==='trust') {
      setShareEmailSubject(shareTrustEmailSubject);
      setShareEmailMessage(shareTrustEmailMessage);
      return;
    }
    setShareEmailSubject(shareStandardEmailSubject);
    setShareEmailMessage(shareStandardEmailMessage);
  };
  const openShareDialog=async (preview)=> {
    setShareError('');
    setShareStageMessages([]);
    setSelectedSharePreview(preview);
    setShareDialogOpen(true);
    setShareDraftLoading(true);
    const result=await cmsService.getPreviewShareDraft(preview.previewId,adminPasscode);
    setShareDraftLoading(false);
    if (result.success) {
      setShareEmailRecipients(result.data.clientEmail || '');
      setShareSmsRecipients(result.data.clientPhone?.trim() || DEFAULT_PHONE_PREFIX);
      const standardSubject=result.data.standardEmailSubject || result.data.emailSubject || '';
      const standardMessage=result.data.standardEmailMessage || result.data.emailMessage || '';
      const trustSubject=result.data.trustEmailSubject || standardSubject;
      const trustMessage=result.data.trustEmailMessage || standardMessage;
      setShareEmailTemplate('standard');
      setShareStandardEmailSubject(standardSubject);
      setShareStandardEmailMessage(standardMessage);
      setShareTrustEmailSubject(trustSubject);
      setShareTrustEmailMessage(trustMessage);
      setShareEmailSubject(standardSubject);
      setShareEmailMessage(standardMessage);
      setShareSmsMessage(result.data.smsMessage || '');
    } else {
      setShareError(result.error || 'Unable to load share draft.');
    }
  };
  const closeShareDialog=()=> {
    if (shareSending) return;
    setShareDialogOpen(false);
    setSelectedSharePreview(null);
    setShareSmsRecipients(DEFAULT_PHONE_PREFIX);
    setShareEmailRecipients('');
  };
  const sendPreviewShare=async (channels)=> {
    if (!selectedSharePreview?.previewId) return;
    if (!shareConsentConfirmed) {
      setShareError('You must confirm the recipient has consented to receive this preview.');
      return;
    }
    if (!shareConsentMethod) {
      setShareError('Please select a consent method.');
      return;
    }
    const normalizedEmailRecipients=normalizeEmailRecipientList(shareEmailRecipients);
    if (channels.includes('email') && !normalizedEmailRecipients) {
      setShareError('Please provide at least one email recipient.');
      return;
    }
    const normalizedSmsRecipients=getValidSmsRecipients(shareSmsRecipients);
    if (channels.includes('sms') && normalizedSmsRecipients.length===0) {
      setShareError(
        'Please enter at least one complete SMS recipient number in international format,for example +61425228778.'
      );
      return;
    }
    const shareRequestId=createShareRequestId();
    setShareSending(true);
    setShareError('');
    setShareStageMessages([]);
    try {
      if (channels.includes('email')) {
        appendShareStage('Preparing email...');
        appendShareStage('Sending email...');
        const emailResult=await cmsService.sendPreviewEmail(
          {
            previewId: selectedSharePreview.previewId,
            shareRequestId,
            channelRequestId: `${shareRequestId}_email`,
            emailRecipients: normalizedEmailRecipients,
            emailSubject: shareEmailSubject,
            emailMessage: shareEmailMessage,
            consentConfirmed: shareConsentConfirmed,
            consentMethod: shareConsentMethod,
          },
          adminPasscode
        );
        if (emailResult.success) {
          appendShareStage('Email sent successfully. Internal CC applied.','success');
        } else {
          appendShareStage(`Email failed: ${emailResult.error}`,'error');
        }
      }
      if (channels.includes('sms')) {
        appendShareStage('Preparing SMS...');
        appendShareStage('Sending SMS...');
        const smsResult=await cmsService.sendPreviewSms(
          {
            previewId: selectedSharePreview.previewId,
            shareRequestId,
            channelRequestId: `${shareRequestId}_sms`,
            smsRecipients: normalizedSmsRecipients.join('\n'),
            smsMessage: shareSmsMessage,
            consentConfirmed: shareConsentConfirmed,
            consentMethod: shareConsentMethod,
          },
          adminPasscode
        );
        if (smsResult.success) {
          appendShareStage('SMS sent successfully. Internal SMS confirmation processed.','success');
        } else {
          appendShareStage(`SMS failed: ${smsResult.error}`,'error');
        }
      }
      const hasError=shareStageMessages.some((m)=> m.status==='error');
      appendShareStage(
        hasError
          ? 'Preview sharing completed with an error. Review the stages above.'
          : 'Preview sharing completed.',
        hasError ? 'error' : 'success'
      );
    } catch (err) {
      setShareError('A network error occurred during the sharing process.');
    } finally {
      setShareSending(false);
    }
  };
  const validatePasscode=(code)=> {
    const trimmed=code.trim();
    return /^\d{10}$/.test(trimmed);
  };
  const handleLogin=async (e)=> {
    e.preventDefault();
    setAuthError('');
    if (!validatePasscode(adminPasscode)) {
      setAuthError('Passcode must be exactly 10 digits.');
      return;
    }
    const normalizedPasscode=adminPasscode.trim();
    setLoading(true);
    const result=await cmsService.validatePreviewAdmin(normalizedPasscode);
    setLoading(false);
    if (result.success) {
      setAdminPasscode(normalizedPasscode);
      setIsAuthenticated(true);
      savePreviewAdminSession(normalizedPasscode);
      loadPreviews(normalizedPasscode);
    } else {
      setAuthError(result.error);
    }
  };
  const togglePreviewOutreachApproval=async (preview)=> {
    if (!preview?.previewId || outreachApprovalUpdating) {
      return;
    }
    setOutreachApprovalUpdating(preview.previewId);
    setAuthError('');
    const result=await cmsService.setPreviewOutreachApproved(
      preview.previewId,
      !preview.outreachApproved,
      adminPasscode
    );
    setOutreachApprovalUpdating('');
    if (!result.success) {
      setAuthError(result.error || 'Unable to update outreach approval.');
      return;
    }
    loadPreviews();
  };
  const loadPreviews=async (passcodeOverride=adminPasscode)=> {
    setLoading(true);
    const result=await cmsService.listPreviews(passcodeOverride.trim());
    setLoading(false);
    if (result.success) {
      setPreviews(result.data);
    } else {
      setAuthError(result.error);
    }
  };
  const resetPreviewForm=()=> {
    setEditingPreviewId('');
    setClientName('');
    setClientEmail('');
    setClientPhone(DEFAULT_PHONE_PREFIX);
    setMediaLinks('');
    setPageTitle('');
    setIntroText('');
    setPageTitleManuallyEdited(false);
    setIntroTextManuallyEdited(false);
    setWatermarkText('BPA SOLUTIONS - WATERMARKED PREVIEW');
  };
  const beginEditPreview=async (preview)=> {
    setAuthError('');
    setSuccessMessage('');
    setEditingPreviewLoading(true);
    const result=await cmsService.getPreviewForAdmin(preview.previewId,adminPasscode);
    setEditingPreviewLoading(false);
    if (!result.success) {
      setAuthError(result.error || 'Unable to load preview for editing.');
      return;
    }
    const data=result.data || {};
    const editableMedia=Array.isArray(data.media)
      ? data.media
          .filter((media)=> media.active !==false)
          .sort((a,b)=> Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
          .map((media)=> media.url)
          .filter(Boolean)
      : [];
    setEditingPreviewId(data.previewId || preview.previewId);
    setClientName(data.clientName || data.businessName || '');
    setClientEmail(data.clientEmail || '');
    setClientPhone(data.clientPhone || DEFAULT_PHONE_PREFIX);
    setMediaLinks(editableMedia.join('\n'));
    setPageTitle(data.pageTitle || '');
    setIntroText(data.introText || '');
    setWatermarkText(data.watermarkText || 'BPA SOLUTIONS - WATERMARKED PREVIEW');
    setPageTitleManuallyEdited(true);
    setIntroTextManuallyEdited(true);
    document.getElementById('preview-form-panel')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  const cancelEditPreview=()=> {
    resetPreviewForm();
    setAuthError('');
    setSuccessMessage('');
  };
  const handleCreatePreview=async (e)=> {
    e.preventDefault();
    setSuccessMessage('');
    setAuthError('');
    if (!clientName.trim()) {
      setAuthError('Client or business name is required.');
      return;
    }
    if (!mediaLinks.trim()) {
      setAuthError('At least one media URL is required.');
      return;
    }
    const mediaUrls=parseEditableMediaLinks();
    if (mediaUrls.length===0) {
      setAuthError('Please provide valid media URLs.');
      return;
    }
    const wasEditing=Boolean(editingPreviewId);
    const payload={
      ...(editingPreviewId ? {previewId: editingPreviewId} : {}),
      clientName: clientName.trim(),
      clientEmail: clientEmail.trim(),
      clientPhone: clientPhone.trim(),
      pageTitle: pageTitle.trim() || `${clientName.trim()} - Preview`,
      introText: introText.trim() || 'Here is your watermarked preview. Please review the assets below.',
      watermarkText: watermarkText.trim() || 'BPA SOLUTIONS - WATERMARKED PREVIEW',
      media: mediaUrls.map((url,index)=> ({
        url,
        sortOrder: index + 1,
      })),
      active: true,
    };
    setLoading(true);
    const result=await cmsService.savePreview(payload,adminPasscode);
    setLoading(false);
    if (result.success) {
      const savedPreviewId=result.data.previewId || result.data.preview_id;
      const publicUrl=`${window.location.origin}/preview/${encodeURIComponent(savedPreviewId)}`;
      const newlyCreatedPreview={
        previewId: savedPreviewId,
        clientName: payload.clientName,
        clientEmail: payload.clientEmail,
        clientPhone: payload.clientPhone,
        pageTitle: payload.pageTitle,
      };
      setSuccessMessage(
        wasEditing
          ? `Preview updated successfully! ID: ${savedPreviewId}`
          : `Preview created successfully! ID: ${savedPreviewId}`
      );
      resetPreviewForm();
      loadPreviews();
      if (!wasEditing) {
        setTimeout(()=> {
          setSuccessMessage(
            <div className="space-y-4">
              <div>Preview created successfully! ID: {savedPreviewId}</div>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="text"
                  value={publicUrl}
                  readOnly
                  className="flex-grow bg-paper border border-gold/30 rounded-md px-3 py-2 text-sm"
                />
                <button
                  onClick={()=> {
                    navigator.clipboard.writeText(publicUrl);
                  }}
                  className="luxury-gold-button px-4 py-2 text-xs"
                >
                  <SafeIcon icon={FiCopy} className="w-4 h-4" />
                </button>
                <button
                  onClick={()=> window.open(publicUrl,'_blank','noopener,noreferrer')}
                  className="luxury-subtle-button px-4 py-2 text-xs"
                >
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                </button>
                <button
                  onClick={()=> openShareDialog(newlyCreatedPreview)}
                  className="luxury-gold-button px-4 py-2 text-xs"
                >
                  <SafeIcon icon={FiSend} className="w-4 h-4 mr-2" />
                  Share Preview
                </button>
              </div>
            </div>
          );
        },100);
      }
    } else {
      setAuthError(result.error);
    }
  };
  const handleCopyLink=(previewId)=> {
    const publicUrl=`${window.location.origin}/preview/${encodeURIComponent(previewId)}`;
    navigator.clipboard.writeText(publicUrl);
  };
  const handleOpenPreview=(previewId)=> {
    const publicUrl=`${window.location.origin}/preview/${encodeURIComponent(previewId)}`;
    window.open(publicUrl,'_blank','noopener,noreferrer');
  };
  const openApprovalDialog=()=> {
    setApprovalDialogError('');
    setShowApprovalDialog(true);
  };
  const closeApprovalDialog=()=> {
    if (clientActionLoading) {
      return;
    }
    setApprovalDialogError('');
    setShowApprovalDialog(false);
  };
  const submitApproval=async (event)=> {
    event.preventDefault();
    const normalizedName=approverName.trim();
    const normalizedEmail=approverEmail.trim().toLowerCase();
    if (!normalizedName) {
      setApprovalDialogError('Please enter your full name.');
      return;
    }
    if (!normalizedEmail) {
      setApprovalDialogError('Please enter your email address.');
      return;
    }
    const emailPattern=/^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(normalizedEmail)) {
      setApprovalDialogError('Please enter a valid email address.');
      return;
    }
    setApprovalDialogError('');
    const result=await handleClientAction('preview_approved',{
      approverName: normalizedName,
      approverEmail: normalizedEmail,
      approvalSource: 'public_preview_page',
    });
    if (result !==false) {
      setShowApprovalDialog(false);
      setApproverName('');
      setApproverEmail('');
    }
  };
  const handleClientAction=async (eventType,extraDetails={})=> {
    if (!previewId || clientActionLoading) {
      return false;
    }
    setClientMessage('');
    setClientActionError('');
    setClientActionLoading(eventType);
    const result=await cmsService.logPreviewEvent({
      eventType,
      previewId,
      details: {
        pageTitle: previewData?.pageTitle || '',
        clientName: previewData?.clientName || '',
        ...extraDetails,
      },
    });
    setClientActionLoading('');
    if (!result.success) {
      setClientActionError(
        result.error || 'We could not record your response. Please email support@bpasolutions.au.'
      );
      return false;
    }
    if (eventType==='preview_approved') {
      setClientMessage(
        'Thank you. Your approval has been recorded. BPA Solutions will be in touch regarding final delivery.'
      );
      return true;
    }
    setClientMessage(
      'Your change request has been recorded. Please send any additional details to support@bpasolutions.au.'
    );
    return true;
  };
  const confirmPreviewStatusChange=async ()=> {
    if (!statusDialog?.preview?.previewId || statusUpdating) {
      return;
    }
    setStatusUpdating(true);
    setAuthError('');
    const {preview,action}=statusDialog;
    const result=
      action==='archive'
        ? await cmsService.archivePreview(preview.previewId,adminPasscode)
        : await cmsService.restorePreview(preview.previewId,adminPasscode);
    setStatusUpdating(false);
    if (!result.success) {
      setAuthError(result.error || `Unable to ${action} preview.`);
      return;
    }
    if (action==='archive' && editingPreviewId===preview.previewId) {
      resetPreviewForm();
    }
    setStatusDialog(null);
    setSuccessMessage(
      action==='archive'
        ? `Preview archived successfully! ID: ${preview.previewId}`
        : `Preview restored successfully! ID: ${preview.previewId}`
    );
    loadPreviews();
  };
  const handleOpenSocialMediaHub=()=> {
    window.open(
      'https://k00.fr/9sfqrwad',
      '_blank',
      'noopener,noreferrer'
    );
  };
  useEffect(()=> {
    if (mode==='admin' && authRestoring) {
      const restoreAdminSession=async ()=> {
        try {
          const session=readPreviewAdminSession();
          if (!session || !session.adminPasscode) {
            setAuthRestoring(false);
            return;
          }
          const storedPasscode=String(session.adminPasscode || '').trim();
          if (!/^\d{10}$/.test(storedPasscode)) {
            clearPreviewAdminSession();
            setAuthRestoring(false);
            return;
          }
          const result=await cmsService.validatePreviewAdmin(storedPasscode);
          if (result.success) {
            setAdminPasscode(storedPasscode);
            setIsAuthenticated(true);
            loadPreviews(storedPasscode);
          } else {
            clearPreviewAdminSession();
            setIsAuthenticated(false);
            setAdminPasscode('');
          }
        } catch (error) {
          console.warn('[PreviewPortal] Session restore failed:',error);
          clearPreviewAdminSession();
          setIsAuthenticated(false);
          setAdminPasscode('');
        } finally {
          setAuthRestoring(false);
        }
      };
      restoreAdminSession();
    }
  },[mode,authRestoring]);
  useEffect(()=> {
    if (mode==='preview' && previewId) {
      const loadPreview=async ()=> {
        setLoading(true);
        const result=await cmsService.fetchPreview(previewId);
        setLoading(false);
        if (result.success) {
          setPreviewData(result.data);
        } else {
          setPreviewError(result.error);
        }
      };
      loadPreview();
    }
  },[mode,previewId]);
  useEffect(()=> {
    if (mode==='preview') {
      const handleContextMenu=(e)=> {
        e.preventDefault();
      };
      const handleDragStart=(e)=> {
        e.preventDefault();
      };
      document.addEventListener('contextmenu',handleContextMenu);
      document.addEventListener('dragstart',handleDragStart);
      return ()=> {
        document.removeEventListener('contextmenu',handleContextMenu);
        document.removeEventListener('dragstart',handleDragStart);
      };
    }
  },[mode]);
  if (mode==='admin' && !isAuthenticated) {
    if (authRestoring) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-paper text-espresso p-6">
          <div className="max-w-md w-full luxury-panel p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiLock} className="w-8 h-8 text-gold" />
              </div>
              <h1 className="text-3xl luxury-serif-heading mb-2">Preview Portal</h1>
              <p className="text-sm text-muted">Restoring admin session...</p>
            </div>
            <div className="flex justify-center py-4">
              <div className="w-6 h-6 rounded-full border-2 border-gold border-t-transparent animate-spin"></div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper text-espresso p-6">
        <div className="max-w-md w-full luxury-panel p-10">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiLock} className="w-8 h-8 text-gold" />
            </div>
            <h1 className="text-3xl luxury-serif-heading mb-2">Preview Portal</h1>
            <p className="text-sm text-muted">Admin Authentication Required</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="relative">
              <input
                id="preview-admin-passcode"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="10"
                value={adminPasscode}
                onChange={(e)=> {
                  setAdminPasscode(e.target.value.replace(/\D/g,''));
                  if (authError) {
                    setAuthError('');
                  }
                }}
                placeholder=" "
                autoComplete="current-password"
                aria-label="Admin passcode"
                className="peer relative z-10 w-full bg-transparent border-b border-gold/30 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso"
                required
              />
              <label
                htmlFor="preview-admin-passcode"
                className="pointer-events-none absolute left-0 -top-3.5 text-[10px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-[10px] peer-focus:text-gold font-medium"
              >
                Admin Passcode (10 digits) *
              </label>
            </div>
            {authError && (
              <div className="flex items-center gap-2 text-red-400 text-xs bg-red-900/20 p-3 border border-red-800/50 rounded-soft">
                <SafeIcon icon={FiAlertCircle} className="flex-shrink-0" />
                {authError}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full luxury-gold-button disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? 'Authenticating...' : 'Access Portal'}
            </button>
          </form>
        </div>
      </div>
    );
  }
  if (mode==='admin' && isAuthenticated) {
    return (
      <>
        <div className="min-h-screen bg-paper text-espresso px-4 py-4 md:px-6 md:py-5">
          <div className="max-w-[1500px] mx-auto space-y-6 h-full flex flex-col">
            <div className="flex items-center justify-between luxury-panel px-5 md:px-7 py-4 shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiEye} className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <h1 className="text-xl luxury-serif-heading">Preview Portal</h1>
                  <p className="text-[10px] text-muted uppercase tracking-wider">Admin Dashboard</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleOpenSocialMediaHub}
                  className="luxury-subtle-button px-4 py-2 text-[10px]"
                >
                  <SafeIcon icon={FiInstagram} className="w-4 h-4 mr-2" />
                  Social Media Hub
                </button>
                <button
                  type="button"
                  onClick={()=> setIntroOutreachOpen(true)}
                  className="luxury-gold-button px-4 py-2 text-[10px]"
                >
                  Send Introduction
                </button>
                <button
                  type="button"
                  onClick={()=> setTransformationStudioOpen(true)}
                  className="luxury-subtle-button px-4 py-2 text-[10px]"
                >
                  Transformation Studio
                </button>
                <button
                  onClick={()=> {
                    setIsAuthenticated(false);
                    setAdminPasscode('');
                    setPreviews([]);
                    clearPreviewAdminSession();
                  }}
                  className="luxury-subtle-button px-4 py-2 text-xs"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)] gap-8 xl:h-[calc(100vh-9.5rem)] xl:min-h-0">
              <div
                id="preview-form-panel"
                className="luxury-panel p-0 flex flex-col min-h-0 xl:h-full xl:overflow-hidden"
              >
                <div className="flex items-center gap-3 px-5 md:px-6 pt-5 md:pt-6 mb-4 md:mb-6 shrink-0">
                  <SafeIcon
                    icon={editingPreviewId ? FiEye : FiPlus}
                    className="w-5 h-5 text-gold"
                  />
                  <h2 className="text-xl luxury-serif-heading">
                    {editingPreviewId ? 'Edit Preview' : 'Create New Preview'}
                  </h2>
                </div>
                <form onSubmit={handleCreatePreview} className="flex flex-col flex-1 min-h-0">
                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 md:px-6 pb-6 space-y-6 pr-1">
                    {editingPreviewId && (
                      <div className="flex items-center justify-between px-4 py-3 bg-gold/5 border border-gold/20 rounded-xl mb-4">
                        <span className="text-[10px] text-gold font-mono font-bold tracking-widest">
                          Preview ID: {editingPreviewId}
                        </span>
                        <button
                          type="button"
                          onClick={cancelEditPreview}
                          className="text-[10px] uppercase tracking-widest text-muted hover:text-gold transition-colors font-bold"
                        >
                          Cancel Edit
                        </button>
                      </div>
                    )}
                    {editingPreviewLoading && (
                      <div className="flex items-center justify-center py-4 gap-3 bg-paper/50 backdrop-blur-sm rounded-xl border border-gold/10 mb-4">
                        <div className="w-4 h-4 rounded-full border border-gold border-t-transparent animate-spin" />
                        <span className="text-[10px] uppercase tracking-widest text-gold font-bold">
                          Loading Preview Data...
                        </span>
                      </div>
                    )}
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-gold"></div>
                        <span className="text-[10px] uppercase tracking-widest text-gold font-bold">
                          Client Profile
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="relative">
                          <input
                            type="text"
                            value={clientName}
                            onChange={(event)=> {
                              const nextClientName=event.target.value;
                              setClientName(nextClientName);
                              if (!pageTitleManuallyEdited) {
                                setPageTitle(buildDefaultPageTitle(nextClientName));
                              }
                              if (!introTextManuallyEdited) {
                                setIntroText(buildDefaultIntroText(nextClientName));
                              }
                            }}
                            placeholder=" "
                            className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-2 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm"
                            required
                          />
                          <label
                            className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"
                          >
                            Client or Business Name *
                          </label>
                        </div>
                        <div className="relative">
                          <input
                            type="email"
                            value={clientEmail}
                            onChange={(e)=> setClientEmail(e.target.value)}
                            onBlur={()=> setClientEmail(clientEmail.trim().toLowerCase())}
                            placeholder=" "
                            className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-2 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm"
                          />
                          <label
                            className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"
                          >
                            Client Email (Optional)
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="relative">
                        <input
                          type="tel"
                          value={clientPhone}
                          onChange={(event)=>
                            setClientPhone(event.target.value.replace(/[^\d+\s()-]/g,''))
                          }
                          onBlur={()=> setClientPhone(normalizeAustralianPhone(clientPhone))}
                          placeholder="+61425228778"
                          className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-2 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm"
                        />
                        <label
                          className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"
                        >
                          Client Mobile (Optional)
                        </label>
                        <p className="text-[9px] text-muted font-light mt-1">
                          Use international format: +61425228778.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-6 pt-2">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-gold"></div>
                        <span className="text-[10px] uppercase tracking-widest text-gold font-bold">
                          Media Assets
                        </span>
                      </div>
                      <div className="relative">
                        <textarea
                          value={mediaLinks}
                          onChange={(e)=> setMediaLinks(e.target.value)}
                          placeholder=" "
                          className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-2 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent resize-none text-espresso caret-espresso text-sm min-h-[110px] md:min-h-[125px]"
                          required
                        />
                        <label
                          className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"
                        >
                          Media Links * (one URL per line)
                        </label>
                        <p className="text-[9px] text-muted font-light mt-1 leading-relaxed">
                          Paste one hosted image or video URL per line. Add a line to add media,remove a line to delete media,or reorder lines to change the gallery order.
                        </p>
                        {editingPreviewId && (
                          <div className="mt-4 flex items-center justify-between bg-gold/5 border border-gold/10 rounded-xl p-3">
                            <span className="text-[10px] text-muted font-medium uppercase tracking-wider">
                              {parseEditableMediaLinks().length} media items currently staged
                            </span>
                            <button
                              type="button"
                              onClick={()=> setMediaReviewOpen(true)}
                              className="luxury-subtle-button px-4 py-1.5 text-[9px] h-auto"
                            >
                              Review Media Visually
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-6 pt-2">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-gold"></div>
                        <span className="text-[10px] uppercase tracking-widest text-gold font-bold">
                          Presentation
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="relative">
                          <input
                            type="text"
                            value={pageTitle}
                            onChange={(event)=> {
                              setPageTitle(event.target.value);
                              setPageTitleManuallyEdited(true);
                            }}
                            placeholder=" "
                            className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-2 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm"
                          />
                          <label
                            className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"
                          >
                            Page Title
                          </label>
                          <p className="text-[9px] text-muted font-light mt-1">
                            Auto-filled from the client name. You may replace it if required.
                          </p>
                        </div>
                        <div className="relative">
                          <input
                            type="text"
                            value={watermarkText}
                            onChange={(e)=> setWatermarkText(e.target.value)}
                            placeholder=" "
                            className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-2 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm"
                          />
                          <label
                            className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"
                          >
                            Watermark Text
                          </label>
                        </div>
                      </div>
                      <div className="relative">
                        <textarea
                          value={introText}
                          onChange={(event)=> {
                            setIntroText(event.target.value);
                            setIntroTextManuallyEdited(true);
                          }}
                          placeholder=" "
                          className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-2 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent resize-none text-espresso caret-espresso text-sm min-h-[82px] md:min-h-[96px]"
                        />
                        <label
                          className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"
                        >
                          Intro Text
                        </label>
                        <p className="text-[9px] text-muted font-light mt-1">
                          Auto-filled from the client name. You may replace it if required.
                        </p>
                      </div>
                    </div>
                    {authError && (
                      <div className="flex items-center gap-2 text-red-500 text-[10px] bg-red-50 p-3 border border-red-200 rounded-soft">
                        <SafeIcon icon={FiAlertCircle} className="flex-shrink-0" />
                        {authError}
                      </div>
                    )}
                    {successMessage && (
                      <div className="flex items-center gap-2 text-gold text-[10px] bg-gold/5 p-3 border border-gold/20 rounded-soft">
                        <SafeIcon icon={FiCheckCircle} className="flex-shrink-0" />
                        {successMessage}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 border-t border-gold/15 bg-ivory/95 backdrop-blur-sm px-5 md:px-6 py-4">
                    <button
                      type="submit"
                      disabled={loading || editingPreviewLoading}
                      className="w-full luxury-gold-button disabled:opacity-70 disabled:cursor-not-allowed py-3 text-xs"
                    >
                      {loading
                        ? editingPreviewId
                          ? 'Saving Changes...'
                          : 'Creating Preview...'
                        : editingPreviewId
                        ? 'Save Changes'
                        : 'Create Preview'}
                    </button>
                  </div>
                </form>
              </div>
              <div className="luxury-panel p-0 flex flex-col min-h-0 xl:h-full xl:overflow-hidden">
                <div className="flex items-center justify-between px-5 md:px-6 py-5 border-b border-gold/15 mb-2 shrink-0">
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiList} className="w-5 h-5 text-gold" />
                    <h2 className="text-lg luxury-serif-heading">Previous Previews</h2>
                  </div>
                  <button
                    onClick={()=> loadPreviews()}
                    disabled={loading}
                    className="luxury-subtle-button px-3 py-1.5 text-[10px] disabled:opacity-70"
                  >
                    Refresh List
                  </button>
                </div>
                {loading && previews.length===0 && (
                  <div className="flex justify-center py-12">
                    <div className="w-8 h-8 rounded-full border-2 border-gold border-t-transparent animate-spin"></div>
                  </div>
                )}
                {!loading && previews.length===0 && (
                  <p className="text-center text-muted text-sm py-12">No previews created yet.</p>
                )}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 md:px-6 pb-6 space-y-3 pr-1">
                  {previews.map((preview)=> {
                    const isArchived=String(preview.status || '').toLowerCase()==='archived';
                    return (
                      <div
                        key={preview.previewId}
                        className={`p-4 border transition-colors duration-300 rounded-soft flex gap-4 items-start ${
                          isArchived
                            ? 'bg-cream/30 border-champagne/30'
                            : 'bg-cream/70 border-champagne/50 hover:border-gold/40'
                        }`}
                      >
                        <PreviousPreviewMiniMedia previewId={preview.previewId} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div className="min-w-0">
                              <h3 className="font-serif text-base text-espresso mb-1 truncate">
                                {preview.clientName}
                              </h3>
                              {preview.clientEmail && (
                                <p className="text-[10px] text-muted mb-1 flex items-center gap-1 truncate">
                                  <SafeIcon icon={FiMail} className="w-2.5 h-2.5" />
                                  {preview.clientEmail}
                                </p>
                              )}
                              {preview.clientPhone && (
                                <p className="text-[10px] text-muted mb-1 flex items-center gap-1 truncate">
                                  <SafeIcon icon={FiPhone} className="w-2.5 h-2.5" />
                                  {preview.clientPhone}
                                </p>
                              )}
                              <p className="text-[10px] text-mutedGold truncate">
                                {preview.pageTitle || 'Untitled Preview'}
                              </p>
                            </div>
                            <span
                              className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ml-2 ${
                                isArchived
                                  ? 'bg-muted/10 text-muted'
                                  : 'bg-gold/10 text-gold'
                              }`}
                            >
                              {preview.status || 'active'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-[10px] text-muted">
                              {preview.mediaCount ?? preview.media?.length ?? 0} media • Updated:{' '}
                              {new Date(preview.updatedAt || preview.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {!isArchived && (
                              <>
                                <button
                                  onClick={()=> handleOpenPreview(preview.previewId)}
                                  className="flex-1 min-w-[80px] luxury-gold-button px-2 py-2 text-[10px]"
                                >
                                  Open
                                </button>
                                <button
                                  onClick={()=> handleCopyLink(preview.previewId)}
                                  className="flex-1 min-w-[80px] luxury-subtle-button px-2 py-2 text-[10px]"
                                >
                                  Copy Link
                                </button>
                                <button
                                  onClick={()=> openShareDialog(preview)}
                                  className="flex-1 min-w-[80px] luxury-gold-button px-2 py-2 text-[10px]"
                                >
                                  Share
                                </button>
                              </>
                            )}
                            <button
                              onClick={()=> beginEditPreview(preview)}
                              className="flex-1 min-w-[80px] luxury-subtle-button px-2 py-2 text-[10px]"
                            >
                              Edit
                            </button>
                            {!isArchived ? (
                              <button
                                onClick={()=> setStatusDialog({preview,action: 'archive'})}
                                className="flex-1 min-w-[80px] border border-red-200 text-red-700 hover:bg-red-50 px-2 py-2 text-[10px] font-medium rounded-full transition-all"
                              >
                                Archive
                              </button>
                            ) : (
                              <button
                                onClick={()=> setStatusDialog({preview,action: 'restore'})}
                                className="flex-1 min-w-[80px] border border-gold/30 text-gold hover:bg-gold/5 px-2 py-2 text-[10px] font-medium rounded-full transition-all"
                              >
                                Restore
                              </button>
                            )}
                          </div>
                          {!isArchived && (
                            <button
                              type="button"
                              onClick={()=> togglePreviewOutreachApproval(preview)}
                              disabled={outreachApprovalUpdating===preview.previewId}
                              className={`mt-3 w-full rounded-full border px-3 py-2 text-[9px] font-bold uppercase tracking-widest transition-all disabled:opacity-60 ${
                                preview.outreachApproved
                                  ? 'border-gold bg-gold/10 text-gold'
                                  : 'border-gold/25 text-muted hover:bg-gold/5 hover:text-gold'
                              }`}
                            >
                              {outreachApprovalUpdating===preview.previewId
                                ? 'Updating...'
                                : preview.outreachApproved
                                ? 'Approved for Outreach Use'
                                : 'Approve for Outreach Use'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
        <IntroOutreachComposer
          isOpen={introOutreachOpen}
          onClose={()=> setIntroOutreachOpen(false)}
          adminPasscode={adminPasscode}
        />
        <TransformationSnapshotStudio
          isOpen={transformationStudioOpen}
          onClose={()=> setTransformationStudioOpen(false)}
        />
        {mediaReviewOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center bg-espresso/65 backdrop-blur-md px-4 py-6">
            <div className="w-full max-w-5xl rounded-[32px] border border-gold/30 bg-ivory shadow-2xl flex flex-col max-h-full overflow-hidden">
              <div className="shrink-0 border-b border-gold/15 bg-paper px-6 py-5 sm:px-8 flex justify-between items-center sticky top-0 z-10">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiEye} className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h2 className="text-xl luxury-serif-heading text-espresso">Visual Media Review</h2>
                    <p className="text-[10px] text-muted uppercase tracking-wider font-bold">
                      Preview ID: {editingPreviewId}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={()=> {
                      const publicUrl=`${window.location.origin}/preview/${encodeURIComponent(
                        editingPreviewId
                      )}`;
                      window.open(publicUrl,'_blank','noopener,noreferrer');
                    }}
                    className="luxury-subtle-button px-4 py-2 text-[10px]"
                  >
                    Open Public Preview
                  </button>
                  <button
                    onClick={()=> setMediaReviewOpen(false)}
                    className="p-2 text-muted hover:text-espresso transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-6 h-6" />
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-8 sm:px-8 overscroll-contain">
                <div className="mb-8 p-4 bg-gold/5 border border-gold/20 rounded-2xl">
                  <p className="text-xs text-espresso leading-relaxed">
                    <span className="font-bold text-gold uppercase tracking-widest text-[10px] block mb-1">
                      Notice
                    </span>
                    Changes shown here are staged only. Nothing is removed from the saved client preview until you click{' '}
                    <strong>Save Changes</strong> in the main edit form.
                  </p>
                </div>
                {parseEditableMediaLinks().length===0 ? (
                  <div className="py-20 text-center">
                    <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-muted/40" />
                    </div>
                    <p className="text-sm text-muted max-w-xs mx-auto leading-relaxed italic">
                      No media URLs are currently staged. Close this panel and add a hosted image or video URL in the Media Links field.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {parseEditableMediaLinks().map((url,index,allUrls)=> {
                      const isVideo=isLikelyVideoUrl(url);
                      return (
                        <div key={`${url}-${index}`} className="flex flex-col gap-4">
                          <div className="relative luxury-media-card aspect-[9/16] bg-cream group">
                            <div className="absolute top-3 left-3 bg-ink/80 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 z-10 flex items-center gap-2">
                              <span className="text-[9px] font-bold text-gold">{index + 1}</span>
                              <span className="text-[9px] uppercase tracking-widest text-soft">
                                {isVideo ? 'Video' : 'Image'}
                              </span>
                            </div>
                            {isVideo ? (
                              <video
                                src={url}
                                className="w-full h-full object-cover"
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="metadata"
                                disablePictureInPicture
                                controlsList="nodownload noremoteplayback nofullscreen"
                                onContextMenu={(e)=> e.preventDefault()}
                              />
                            ) : (
                              <img
                                src={url}
                                alt={`Preview media ${index + 1}`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                draggable={false}
                                onContextMenu={(e)=> e.preventDefault()}
                              />
                            )}
                            <div className="absolute inset-0 bg-ink/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center p-6 gap-3 z-20">
                              <button
                                onClick={()=> window.open(url,'_blank','noopener,noreferrer')}
                                className="w-full luxury-gold-button py-2 px-4 text-[10px]"
                              >
                                Open Original
                              </button>
                              <div className="flex gap-2 w-full">
                                <button
                                  onClick={()=> moveEditableMedia(index,-1)}
                                  disabled={index===0}
                                  className="flex-1 luxury-subtle-button flex items-center justify-center py-2 px-2 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Move Up"
                                >
                                  <SafeIcon icon={FiArrowUp} className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={()=> moveEditableMedia(index,1)}
                                  disabled={index===allUrls.length - 1}
                                  className="flex-1 luxury-subtle-button flex items-center justify-center py-2 px-2 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Move Down"
                                >
                                  <SafeIcon icon={FiArrowDown} className="w-4 h-4" />
                                </button>
                              </div>
                              <button
                                onClick={()=> requestEditableMediaRemoval(index)}
                                className="w-full border border-red-500/50 bg-red-500/10 text-red-200 hover:bg-red-500/20 py-2 px-4 text-[10px] uppercase tracking-widest font-bold rounded-full transition-all"
                              >
                                Remove from List
                              </button>
                            </div>
                          </div>
                          <div className="px-2">
                            <p className="text-[9px] text-muted truncate font-mono bg-cream/50 px-2 py-1 rounded border border-gold/10">
                              {url}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="shrink-0 border-t border-gold/15 bg-ivory px-6 py-5 sm:px-8 flex justify-end">
                <button
                  onClick={()=> setMediaReviewOpen(false)}
                  className="luxury-gold-button px-8 py-3 text-xs"
                >
                  Done Reviewing
                </button>
              </div>
            </div>
          </div>
        )}
        {pendingMediaRemovalIndex !==null && (
          <div className="fixed inset-0 z-[130] flex items-center justify-center bg-espresso/75 backdrop-blur-sm px-4">
            <div className="w-full max-w-sm rounded-[24px] border border-gold/30 bg-ivory shadow-2xl p-8">
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiTrash2} className="w-7 h-7" />
                </div>
                <h3 className="text-xl luxury-serif-heading text-espresso mb-2">Remove Media?</h3>
                <p className="text-sm text-muted leading-relaxed">
                  Remove this media item from the edit list? This change is staged only and won't affect the client preview until you save.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={confirmEditableMediaRemoval}
                  className="w-full bg-red-600 text-white hover:bg-red-700 px-6 py-3 text-xs uppercase tracking-widest font-bold rounded-full transition-all"
                >
                  Remove from Edit List
                </button>
                <button
                  onClick={cancelEditableMediaRemoval}
                  className="w-full px-6 py-3 text-xs uppercase tracking-widest font-bold border border-gold/30 text-muted hover:bg-gold/5 rounded-full transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {statusDialog && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-espresso/55 backdrop-blur-sm px-4">
            <div className="w-full max-w-md rounded-[24px] border border-gold/30 bg-ivory shadow-2xl p-8">
              <div className="text-center mb-6">
                <div
                  className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    statusDialog.action==='archive'
                      ? 'bg-red-50 text-red-600'
                      : 'bg-gold/10 text-gold'
                  }`}
                >
                  <SafeIcon
                    icon={statusDialog.action==='archive' ? FiAlertCircle : FiCheckCircle}
                    className="w-7 h-7"
                  />
                </div>
                <h2 className="text-2xl luxury-serif-heading text-espresso mb-2">
                  {statusDialog.action==='archive' ? 'Archive Preview' : 'Restore Preview'}
                </h2>
                <p className="text-sm text-muted">
                  {statusDialog.action==='archive'
                    ? 'Are you sure you want to archive this preview? It will no longer be available to the client publicly.'
                    : 'Restore this preview to make it available to the client again.'}
                </p>
              </div>
              <div className="bg-cream/50 rounded-xl p-4 mb-8 border border-gold/10">
                <p className="text-[10px] uppercase tracking-widest text-muted font-bold mb-1">
                  Preview Details
                </p>
                <p className="text-sm font-bold text-espresso">{statusDialog.preview.clientName}</p>
                <p className="text-[10px] font-mono text-muted mt-1">
                  ID: {statusDialog.preview.previewId}
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={confirmPreviewStatusChange}
                  disabled={statusUpdating}
                  className={`w-full px-6 py-3 text-xs uppercase tracking-widest font-bold rounded-full transition-all ${
                    statusDialog.action==='archive'
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'luxury-gold-button'
                  }`}
                >
                  {statusUpdating
                    ? `Updating Status...`
                    : `Confirm ${statusDialog.action==='archive' ? 'Archive' : 'Restore'}`}
                </button>
                <button
                  onClick={()=> setStatusDialog(null)}
                  disabled={statusUpdating}
                  className="w-full px-6 py-3 text-xs uppercase tracking-widest font-bold border border-gold/30 text-muted hover:bg-gold/5 rounded-full transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {shareDialogOpen && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-espresso/55 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
          >
            <div className="w-full max-w-2xl rounded-[28px] border border-gold/30 bg-ivory shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="border-b border-gold/20 px-6 py-5 sm:px-8 flex justify-between items-center bg-paper sticky top-0 z-10">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-gold">Share Private Preview</p>
                  <h2 className="mt-2 font-serif text-2xl text-espresso">
                    {selectedSharePreview?.clientName || 'Client Preview'}
                  </h2>
                </div>
                <button
                  onClick={closeShareDialog}
                  disabled={shareSending}
                  className="p-2 text-muted hover:text-espresso transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>
              <div className="px-6 py-8 sm:px-8 space-y-8">
                {shareDraftLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin"></div>
                    <p className="text-sm text-muted">Loading share draft...</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-cream/50 border border-gold/20 rounded-xl p-4 flex gap-4 items-center">
                      <PreviousPreviewMiniMedia previewId={selectedSharePreview?.previewId} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-espresso truncate">
                          {selectedSharePreview?.pageTitle}
                        </p>
                        <p className="text-[10px] text-muted font-mono mt-1">
                          ID: {selectedSharePreview?.previewId}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <SafeIcon icon={FiExternalLink} className="w-3 h-3 text-gold" />
                          <p className="text-[10px] text-gold truncate">
                            {window.location.origin}/preview/{selectedSharePreview?.previewId}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted">
                            Email Recipients
                          </label>
                          <textarea
                            value={shareEmailRecipients}
                            onChange={(e)=> setShareEmailRecipients(e.target.value)}
                            onBlur={()=> setShareEmailRecipients(normalizeEmailRecipientList(shareEmailRecipients))}
                            className="w-full rounded-xl border border-gold/30 bg-white/70 px-4 py-3 text-espresso caret-espresso outline-none transition focus:border-gold text-sm h-24 resize-none"
                            placeholder="email@example.com"
                          />
                          <p className="text-[10px] text-muted">
                            You may add or replace email addresses before sending.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted">
                            SMS Recipients
                          </label>
                          <textarea
                            value={shareSmsRecipients}
                            onChange={(event)=> {
                              setShareSmsRecipients(event.target.value.replace(/[^\d+\s(),;\n-]/g,''));
                              if (shareError) {
                                setShareError('');
                              }
                            }}
                            onBlur={()=> setShareSmsRecipients(normalizeSmsRecipientList(shareSmsRecipients))}
                            className="w-full rounded-xl border border-gold/30 bg-white/70 px-4 py-3 text-espresso caret-espresso outline-none transition focus:border-gold text-sm h-24 resize-none"
                            placeholder="+61400000000"
                          />
                          <p className="text-[10px] text-muted">
                            Use international format,for example +61425228778.
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted">
                          Email Template
                        </label>
                        <div className="flex gap-3">
                          <button
                            onClick={()=> applyShareEmailTemplate('standard')}
                            className={`flex-1 px-4 py-2.5 rounded-xl text-[10px] uppercase tracking-widest font-bold transition-all border ${
                              shareEmailTemplate==='standard'
                                ? 'bg-gold text-espresso border-gold shadow-sm'
                                : 'bg-white/50 text-muted border-gold/20 hover:border-gold/40'
                            }`}
                          >
                            Standard Follow-Up
                          </button>
                          <button
                            onClick={()=> applyShareEmailTemplate('trust')}
                            className={`flex-1 px-4 py-2.5 rounded-xl text-[10px] uppercase tracking-widest font-bold transition-all border ${
                              shareEmailTemplate==='trust'
                                ? 'bg-gold text-espresso border-gold shadow-sm'
                                : 'bg-white/50 text-muted border-gold/20 hover:border-gold/40'
                            }`}
                          >
                            Trust & Verification
                          </button>
                        </div>
                        <p className="text-[9px] text-muted leading-relaxed">
                          Use the standard version for normal follow-ups. Use the trust-verification version only when the recipient wants to independently verify BPA Solutions.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted">
                          Email Subject
                        </label>
                        <input
                          type="text"
                          value={shareEmailSubject}
                          onChange={(e)=> setShareEmailSubject(e.target.value)}
                          className="w-full rounded-xl border border-gold/30 bg-white/70 px-4 py-3 text-espresso caret-espresso outline-none transition focus:border-gold text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted">
                          Email Message
                        </label>
                        <textarea
                          value={shareEmailMessage}
                          onChange={(e)=> setShareEmailMessage(e.target.value)}
                          className="w-full rounded-xl border border-gold/30 bg-white/70 px-4 py-3 text-espresso caret-espresso outline-none transition focus:border-gold text-sm h-32 resize-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted">
                          SMS Message
                        </label>
                        <textarea
                          value={shareSmsMessage}
                          onChange={(e)=> setShareSmsMessage(e.target.value)}
                          className="w-full rounded-xl border border-gold/30 bg-white/70 px-4 py-3 text-espresso caret-espresso outline-none transition focus:border-gold text-sm h-24 resize-none"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center pt-4 border-t border-gold/20">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="share-consent"
                            checked={shareConsentConfirmed}
                            onChange={(e)=> setShareConsentConfirmed(e.target.checked)}
                            className="mt-1 w-4 h-4 accent-gold rounded border-gold/30"
                          />
                          <label
                            htmlFor="share-consent"
                            className="text-[10px] text-muted uppercase tracking-wider leading-relaxed cursor-pointer select-none"
                          >
                            The recipient agreed to receive this private preview by email and/or SMS during our conversation.
                          </label>
                        </div>
                        <div className="relative">
                          <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted absolute -top-3 left-4 bg-ivory px-2">
                            Consent Method
                          </label>
                          <select
                            value={shareConsentMethod}
                            onChange={(e)=> setShareConsentMethod(e.target.value)}
                            className="w-full rounded-xl border border-gold/30 bg-white/70 px-4 py-3 text-espresso outline-none transition focus:border-gold text-sm appearance-none"
                          >
                            <option value="Phone call">Phone call</option>
                            <option value="Client request">Client request</option>
                            <option value="Existing client">Existing client</option>
                            <option value="Other">Other</option>
                          </select>
                          <SafeIcon
                            icon={FiChevronDown}
                            className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gold pointer-events-none"
                          />
                        </div>
                      </div>
                    </div>
                    {(shareStageMessages.length > 0 || shareError) && (
                      <div className="bg-espresso text-soft rounded-xl p-5 space-y-3 shadow-inner">
                        <p className="text-[9px] uppercase tracking-[0.2em] text-mutedGold mb-2 font-bold">
                          Status Log
                        </p>
                        {shareStageMessages.map((stage)=> (
                          <div key={stage.id} className="flex items-center gap-3 text-xs">
                            {stage.status==='success' && (
                              <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5 text-green-400" />
                            )}
                            {stage.status==='error' && (
                              <SafeIcon icon={FiAlertCircle} className="w-3.5 h-3.5 text-red-400" />
                            )}
                            {stage.status==='info' && (
                              <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                            )}
                            <span
                              className={
                                stage.status==='error'
                                  ? 'text-red-300'
                                  : stage.status==='success'
                                  ? 'text-green-300'
                                  : 'text-soft/80'
                              }
                            >
                              {stage.message}
                            </span>
                          </div>
                        ))}
                        {shareError && (
                          <div className="flex items-center gap-3 text-xs text-red-400 pt-2 border-t border-white/10 mt-2">
                            <SafeIcon icon={FiAlertCircle} className="w-3.5 h-3.5" />
                            <span>{shareError}</span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 justify-end pt-4">
                      <button
                        onClick={()=> sendPreviewShare(['email'])}
                        disabled={shareSending || shareDraftLoading}
                        className="luxury-subtle-button px-6 py-3 text-xs flex items-center gap-2 disabled:opacity-50"
                      >
                        <SafeIcon icon={FiMail} className="w-4 h-4" />
                        Send Email
                      </button>
                      <button
                        onClick={()=> sendPreviewShare(['sms'])}
                        disabled={shareSending || shareDraftLoading}
                        className="luxury-subtle-button px-6 py-3 text-xs flex items-center gap-2 disabled:opacity-50"
                      >
                        <SafeIcon icon={FiMessageSquare} className="w-4 h-4" />
                        Send SMS
                      </button>
                      <button
                        onClick={()=> sendPreviewShare(['email','sms'])}
                        disabled={shareSending || shareDraftLoading}
                        className="luxury-gold-button px-8 py-3 text-xs flex items-center gap-2 disabled:opacity-50"
                      >
                        <SafeIcon icon={FiSend} className="w-4 h-4" />
                        Send Both
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
  if (mode==='preview') {
    return (
      <div className="min-h-screen bg-paper text-espresso relative">
        <div className="pointer-events-none fixed inset-0 z-[5] overflow-hidden" aria-hidden="true">
          <div className="absolute -inset-[35%] flex rotate-[-25deg] flex-col justify-around gap-12 opacity-[0.055]">
            {Array.from({length: 18}).map((_,index)=> (
              <div
                key={`page-watermark-${index}`}
                className="whitespace-nowrap text-center text-sm font-medium uppercase tracking-[0.42em] text-espresso"
              >
                BPA SOLUTIONS PREVIEW • BPA SOLUTIONS PREVIEW • BPA SOLUTIONS PREVIEW • BPA SOLUTIONS PREVIEW
              </div>
            ))}
          </div>
        </div>
        <header className="sticky top-0 z-50 bg-paper/90 backdrop-blur-xl border-b border-champagne/40 relative z-10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold to-goldDark flex items-center justify-center">
                <span className="text-espressoDeep font-serif font-bold">B</span>
              </div>
              <span className="font-serif tracking-wide">BPA Solutions</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted">Watermarked Preview</span>
              <a href="/" className="luxury-subtle-button px-4 py-2 text-xs">
                Back to BPA Solutions
              </a>
            </div>
          </div>
        </header>
        <main className="py-16 relative z-10">
          {loading && (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 rounded-full border-2 border-gold border-t-transparent animate-spin"></div>
            </div>
          )}
          {previewError && !loading && (
            <div className="max-w-2xl mx-auto px-6">
              <div className="luxury-panel p-8 text-center">
                <div className="w-16 h-16 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="text-2xl luxury-serif-heading mb-4">Preview Not Found</h2>
                <p className="text-muted mb-6">{previewError}</p>
                <a href="/" className="luxury-gold-button">
                  Return to Homepage
                </a>
              </div>
            </div>
          )}
          {previewData && !loading && !previewError && (
            <div className="max-w-6xl mx-auto px-6 space-y-16">
              <div className="luxury-panel p-12 text-center bg-cream/70 border border-champagne/50">
                <h1 className="text-4xl md:text-5xl luxury-serif-heading mb-6 text-champagne">
                  {previewData.pageTitle || 'Your Preview'}
                </h1>
                {previewData.introText && (
                  <p className="text-lg text-muted max-w-3xl mx-auto leading-relaxed mb-6">
                    {previewData.introText}
                  </p>
                )}
                {previewData.clientName && (
                  <p className="text-sm text-gold font-medium">Preview for {previewData.clientName}</p>
                )}
              </div>
              <div className="max-w-3xl mx-auto">
                <div className="bg-gold/10 border border-gold/30 rounded-soft p-6 text-center">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-gold" />
                    <span className="text-gold font-medium uppercase tracking-widest text-sm">
                      Watermarked Preview Only
                    </span>
                  </div>
                  <p className="text-sm text-muted leading-relaxed">
                    Final clean assets are released after approval/payment. These previews are for client review purposes only.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {previewData.media?.map((media,index)=> (
                  <motion.div
                    key={index}
                    initial={{opacity: 0,y: 20}}
                    animate={{opacity: 1,y: 0}}
                    transition={{delay: index * 0.1}}
                    className="luxury-hover-lift"
                  >
                    <MediaCard
                      media={media}
                      watermarkText={previewData.watermarkText || 'BPA SOLUTIONS - WATERMARKED PREVIEW'}
                    />
                  </motion.div>
                ))}
              </div>
              <div className="max-w-2xl mx-auto luxury-panel p-8 text-center bg-cream/70">
                <h3 className="text-2xl luxury-serif-heading mb-3 text-champagne">Ready to proceed?</h3>
                <p className="text-sm text-muted mb-8 leading-relaxed">
                  Approve the preview or send through any requested adjustments.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={openApprovalDialog}
                    disabled={Boolean(clientActionLoading)}
                    className="luxury-gold-button disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {clientActionLoading==='preview_approved'
                      ? 'Recording Approval...'
                      : 'Approve Preview'}
                  </button>
                  <button
                    onClick={()=> handleClientAction('changes_requested')}
                    disabled={Boolean(clientActionLoading)}
                    className="luxury-subtle-button disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {clientActionLoading==='changes_requested'
                      ? 'Recording Request...'
                      : 'Request Changes'}
                  </button>
                </div>
              </div>
              {clientActionError && (
                <div className="max-w-2xl mx-auto">
                  <div className="bg-red-900/20 border border-red-800/50 rounded-soft p-6 text-center">
                    <p className="text-red-300 font-medium">{clientActionError}</p>
                  </div>
                </div>
              )}
              {clientMessage && (
                <div className="max-w-2xl mx-auto">
                  <div className="bg-gold/10 border border-gold/30 rounded-soft p-6 text-center">
                    <p className="text-gold font-medium">{clientMessage}</p>
                  </div>
                </div>
              )}
              <div className="max-w-4xl mx-auto pt-16 mt-16 border-t border-champagne/30">
                <div className="bg-cream/40 rounded-[24px] border border-gold/20 p-8 md:p-10">
                  <div className="text-center mb-8">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-gold font-bold mb-3">
                      Verify BPA Solutions
                    </p>
                    <h3 className="text-xl md:text-2xl luxury-serif-heading text-espresso mb-4">
                      Received an introductory call,email or message from us?
                    </h3>
                    <p className="text-sm text-muted leading-relaxed max-w-2xl mx-auto">
                      You are welcome to independently verify our business details before engaging with us. BPA Solutions will never request passwords,one-time codes or remote access during an introductory conversation.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white/60 rounded-xl p-4 border border-gold/10">
                      <p className="text-[9px] uppercase tracking-widest text-gold mb-1 font-bold">
                        Business
                      </p>
                      <p className="text-sm text-espresso">BPA Solutions</p>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 border border-gold/10">
                      <p className="text-[9px] uppercase tracking-widest text-gold mb-1 font-bold">
                        Operated Through
                      </p>
                      <p className="text-sm text-espresso">Anish Malhotra Pty Ltd</p>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 border border-gold/10">
                      <p className="text-[9px] uppercase tracking-widest text-gold mb-1 font-bold">
                        ABN
                      </p>
                      <p className="text-sm text-espresso">83 679 369 932</p>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 border border-gold/10">
                      <p className="text-[9px] uppercase tracking-widest text-gold mb-1 font-bold">
                        Contact
                      </p>
                      <p className="text-sm text-espresso">Anish Malhotra</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-[11px] font-medium text-muted">
                    <a
                      href="mailto:support@bpasolutions.au"
                      className="hover:text-gold transition-colors flex items-center gap-2"
                    >
                      <SafeIcon icon={FiMail} className="w-3.5 h-3.5" />
                      support@bpasolutions.au
                    </a>
                    <a
                      href="tel:+61425228778"
                      className="hover:text-gold transition-colors flex items-center gap-2"
                    >
                      <SafeIcon icon={FiPhone} className="w-3.5 h-3.5" />
                      +61 425 228 778
                    </a>
                    <a
                      href="https://luxury.bpasolutions.au"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-gold transition-colors flex items-center gap-2"
                    >
                      <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                      luxury.bpasolutions.au
                    </a>
                    <span className="flex items-center gap-2">
                      <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5 text-gold/60" />
                      Sydney,Australia
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
        {showApprovalDialog && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-espresso/55 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="approval-dialog-title"
            onMouseDown={(event)=> {
              if (event.target===event.currentTarget) {
                closeApprovalDialog();
              }
            }}
          >
            <div className="w-full max-w-lg rounded-[28px] border border-gold/30 bg-ivory shadow-2xl">
              <div className="border-b border-gold/20 px-6 py-5 sm:px-8">
                <p className="text-[10px] uppercase tracking-[0.24em] text-gold">Preview Approval</p>
                <h2
                  id="approval-dialog-title"
                  className="mt-2 font-serif text-3xl text-espresso"
                >
                  Confirm your approval
                </h2>
                <p className="mt-3 text-sm leading-6 text-muted">
                  Please provide your details so BPA Solutions can record who approved this preview.
                </p>
              </div>
              <form onSubmit={submitApproval} className="space-y-5 px-6 py-6 sm:px-8">
                <div>
                  <label
                    htmlFor="approval-full-name"
                    className="mb-2 block text-[10px] font-medium uppercase tracking-[0.18em] text-muted"
                  >
                    Full Name *
                  </label>
                  <input
                    id="approval-full-name"
                    type="text"
                    value={approverName}
                    onChange={(event)=> {
                      setApproverName(event.target.value);
                      if (approvalDialogError) {
                        setApprovalDialogError('');
                      }
                    }}
                    autoComplete="name"
                    className="w-full rounded-xl border border-gold/30 bg-white/70 px-4 py-3 text-espresso caret-espresso outline-none transition focus:border-gold"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="approval-email"
                    className="mb-2 block text-[10px] font-medium uppercase tracking-[0.18em] text-muted"
                  >
                    Email Address *
                  </label>
                  <input
                    id="approval-email"
                    type="email"
                    value={approverEmail}
                    onChange={(event)=> {
                      setApproverEmail(event.target.value);
                      if (approvalDialogError) {
                        setApprovalDialogError('');
                      }
                    }}
                    autoComplete="email"
                    className="w-full rounded-xl border border-gold/30 bg-white/70 px-4 py-3 text-espresso caret-espresso outline-none transition focus:border-gold"
                    required
                  />
                </div>
                {approvalDialogError && (
                  <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3">
                    <p className="text-sm text-red-700">{approvalDialogError}</p>
                  </div>
                )}
                <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={closeApprovalDialog}
                    disabled={Boolean(clientActionLoading)}
                    className="rounded-full border border-gold/35 px-6 py-3 text-xs uppercase tracking-[0.16em] text-espresso transition hover:bg-gold/10 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={Boolean(clientActionLoading)}
                    className="rounded-full bg-gold px-6 py-3 text-xs uppercase tracking-[0.16em] text-espresso transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {clientActionLoading==='preview_approved'
                      ? 'Recording Approval...'
                      : 'Confirm Approval'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        <footer className="mt-24 border-t border-champagne/30 py-12 relative z-10">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-gold to-goldDark flex items-center justify-center">
                <span className="text-espressoDeep font-serif text-sm leading-none">B</span>
              </div>
              <span className="font-serif tracking-wide">BPA Solutions</span>
            </div>
            <p className="text-xs text-muted">Premium jewellery visuals for ecommerce brands</p>
          </div>
        </footer>
      </div>
    );
  }
  return null;
};
export default PreviewPortal;